/* eslint-disable @stylistic/max-len */
import {getDocument, currentUser} from '@/services/FirestoreService';
import type {Feature, Overlay} from '@/services/OverlaysService';
import type {Component, VNode} from 'vue';
import type {GridData} from '@/components/DataTable/types';
import {PostHogService} from '@/services/PostHogService';
import {AlgoliaConfig} from '@/internal/components/FilterMenu/sections/UpsertSharedMap/UpsertSharedMap';
import {TokenService} from '@/services/TokenService';

// Types are defined here because certain class types such as Overlay could not be found when attempting to write these
// types inside a .d.ts file. This may be an issue with our tsconfig, but I didn't work through it. This setup is
// intended to be temporary until types are completed and organized.

interface AddableFeature {
  /** Indicates this Feature can be added at any zoom level. */
  ignoreZoomLevel: boolean;
  type: string;
}

interface MessageCenterMessage {
  actions?: SnackAction[];
  body: string;
  /** Runs when the message is closed without choosing one of its actions. */
  dismissCallback?: () => void;
  header: string;
  messageType: string;
};

interface SnackAction {
  callback: () => void;
  class: string;
  title: string;
}

interface SnackMessage {
  actions?: SnackAction[];
  body: string;
  dismissCallback?: () => void;
  header: string;
  messageType: string;
}

interface SnackbarAction {
  text: string;
  callback: () => unknown;
  /** The callback puts the next message in the snackbar, or clears it, so the snackbar doesn't close after it. */
  setsNextSnack?: boolean;
}

type Snack = {
  action?: SnackbarAction;
  closeCallback?: () => void;
  color?: string;
  duration?: number;
  location?: 'top' | 'bottom';
  message: VNode | string;
};

interface MenuItem {
  clickable?: boolean;
  component: Component;
  id: string;
  keepAlive?: boolean;
  notifications?: {
    /** Imposes a limit on the front-facing notification count. */
    limit?: number;
  };
  tip: string;
  type: 'sheet' | 'bar' | 'dialog';
}

type DebugUser = {
  /** The user's unique ID. */
  id: string;
  /** Whether the user has debug mode enabled. */
  debugEnabled: boolean;
  /** The user's email address. */
  email: string;
};

type Nullable<T> = T | null;

interface AppState {
  allowFeatureGrid: boolean;
  currencyFormatter: Intl.NumberFormat;
  dateFormatter_US: Intl.DateTimeFormat;
  /** A list of UIDs denoting users with escalated debug privileges. */
  debugUsers: string[];
  /** A Feature currently being edited. */
  editingFeature: Nullable<Feature>;
  loadingNewFeature: boolean;
  messages: MessageCenterMessage[];
  numberFormatter: Intl.NumberFormat;
  /** An Overlay currently selected by the user. */
  selectedOverlay: Nullable<Overlay>;
  /** ID of the currently selected menu. */
  selectedMenuItem: Nullable<string>;
  snackbar: Nullable<Snack>;
  subMenus: MenuItem[];
  gridData: Nullable<GridData>;
  algoliaConfig: AlgoliaConfig | null;
  linkedLabelFeature: Nullable<Feature>;
  /** Null when the popup creates a new label. */
  linkedLabelToEdit: Nullable<Feature>;
}

interface BaseMutations {
  /** Appends a message to state for the message center to handle. */
  addMessage: (state: AppState, payload: MessageCenterMessage) => void;
  removeMessage: (state: AppState, payload: MessageCenterMessage) => void;
  /** Clear all messages in state. */
  resetMessages: (state: AppState) => void;
  setDebugUsers: (state: AppState, payload: string[]) => void;
  setEditingFeature: (state: AppState, payload: Feature | null) => void;
  setLoadingNewFeature: (state: AppState, payload: boolean) => void;
  /** Sets the selected menu item. Payload is expected to be the ID of the selected item. */
  setSelectedMenuItem: (state: AppState, payload: string | null) => void;
  setSelectedOverlay: (state: AppState, payload: Overlay | null) => void;
  /** Sets a snackbar for the app. If payload is null, the snackbar will be cleared. */
  setSnack: (state: AppState, payload: Snack | null) => void;
  setGridData: (state: AppState, payload: GridData) => void;
  setAlgoliaConfig: (state: AppState, payload: AlgoliaConfig | null) => void;
  setLinkedLabelFeature: (state: AppState, payload: Feature | null) => void;
  setLinkedLabelToEdit: (state: AppState, payload: Feature | null) => void;
}

interface BaseActions {
  /** Adds a message indicating an error to state. */
  addErrorMessage: (context: BaseActionContext, payload: string) => void;
  /** Invokes mutation for adding a message to state. Messages that are already added are ignored. */
  addMessage: (context: BaseActionContext, payload: SnackMessage) => void;
  /** Adds a message indicating success to state. */
  addSuccessMessage: (context: BaseActionContext, payload: string) => void;
  loadDebugUsers: (context: BaseActionContext, payload: {collection: string}) => Promise<void>;
  startSessionDebugging: (context: BaseActionContext, payload: {collection: string}) => Promise<void>;
  /** Removes a message from state. */
  removeMessage: (context: BaseActionContext, payload: SnackMessage) => void;
  setEditingFeature: (context: BaseActionContext, payload: {feature: Feature; context?: {event: Event}; mode?: string}) => Promise<void>;
  loadAlgoliaConfig: (context: BaseActionContext) => Promise<void>;
}

type BaseActionContext = {
  /**
   * Function to call state mutations.
   * @param key The name of the mutation to call.
   * @param payload The payload to pass to the mutation.
   */
  commit<K extends keyof typeof baseMutations>
  (mutation: K, payload: Parameters<BaseMutations[K]>[1]): ReturnType<typeof baseMutations[K]>;
  /**
   * Function to call state actions.
   * @param key The name of the action to call.
   * @param payload The payload to pass to the action.
   */
  dispatch<K extends keyof typeof baseActions>
  (action: K, payload: Parameters<BaseActions[K]>[1]): ReturnType<typeof baseActions[K]>;
  state: AppState;
};

const baseState: AppState = {
  numberFormatter: new Intl.NumberFormat('en-US'),
  currencyFormatter: new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }),
  dateFormatter_US: new Intl.DateTimeFormat('en-US'),
  messages: [],
  snackbar: null,
  selectedOverlay: null,
  selectedMenuItem: null,
  editingFeature: null,
  loadingNewFeature: false,
  subMenus: [],
  debugUsers: [],
  gridData: null,
  allowFeatureGrid: false,
  algoliaConfig: null,
  linkedLabelFeature: null,
  linkedLabelToEdit: null,
};

const baseMutations: BaseMutations = {
  setLinkedLabelFeature: (s, p) => {
    s.linkedLabelFeature = p;
  },
  setLinkedLabelToEdit: (s, p) => {
    s.linkedLabelToEdit = p;
  },
  setSelectedOverlay: (s, p) => {
    s.selectedOverlay = p;
    if (p) {
      s.selectedMenuItem = 'overlays';
    }
  },
  setSelectedMenuItem: (s, p) => {
    s.selectedMenuItem = p;
  },
  setEditingFeature: (s, p) => {
    s.editingFeature = p;
  },
  setLoadingNewFeature: (s, p) => {
    s.loadingNewFeature = p;
  },
  addMessage: (s, p) => {
    s.messages = s.messages.concat(p);
  },
  removeMessage: (s, p) => {
    const removeIndex = s.messages.findIndex((message) => message === p);
    s.messages.splice(removeIndex, 1);
  },
  resetMessages: (s) => {
    s.messages = [];
  },
  setSnack: (s, p) => {
    s.snackbar = p;
  },
  setDebugUsers: (s, p) => {
    s.debugUsers = p;
  },
  setGridData: (s, p) => {
    s.gridData = p;
  },
  setAlgoliaConfig: (s, p) => {
    s.algoliaConfig = p;
  },
};

const baseActions: BaseActions = {
  addSuccessMessage({dispatch}, payload) {
    dispatch('addMessage', {
      body: payload,
      header: 'Success!',
      messageType: 'success',
    });
  },
  addErrorMessage({dispatch}, payload) {
    dispatch('addMessage', {
      body: payload,
      header: 'Error!',
      messageType: 'danger',
    });
  },
  addMessage({commit, state}, payload) {
    if (!state.messages.find((message) => message === payload)) {
      commit('addMessage', payload);
    }
  },
  removeMessage({commit}, payload) {
    commit('removeMessage', payload);
  },
  async setEditingFeature({commit, state}, payload) {
    // If setEditingFeature is being called with the same feature that is already being edited, do nothing. This fixes
    // a bug with the toolbox where setEditingFeature is called twice in a row when entering edit mode.
    if ((!!state.editingFeature && !!payload) && state.editingFeature?.id === payload?.feature?.id) return;

    // if there is already a feature being edited, stop editing it
    if (state.editingFeature) {
      await (await state.editingFeature.graphic).setEditing(false);
    }
    if (payload?.feature) {
      await (await payload.feature.graphic).setEditing(true, {
        event: payload.context?.event,
      });
    }
    commit('setEditingFeature', payload?.feature);
  },
  async loadDebugUsers({commit}, payload) {
    try {
      const debugUsers = await getDocument(payload.collection, 'debugUsers') as {users: DebugUser[]};
      const userIds = debugUsers?.users?.filter((member) => member.debugEnabled)
        .map((member) => member.id);
      commit('setDebugUsers', userIds);
    } catch (e) {
      console.error('Error getting debug user ids', e);
    }
  },
  async startSessionDebugging({commit, dispatch, state}, payload) {
    if (currentUser()?.uid) {
      await dispatch('loadDebugUsers', payload);
      const user = currentUser();
      if (!user) {
        console.warn('No user is currently logged in. Cannot start session debugging.');
        return;
      }
      if (state.debugUsers?.includes(user.uid)) {
        if (!PostHogService?.initialized) {
          console.warn(`PostHog not initialized for ${process.env.NODE_ENV} environment.`);
          return;
        }
        PostHogService.identifyUser(user);
        PostHogService.startSessionRecording();
        commit('setSnack', {message: 'Debug session recording is in progress'});
      }
    }
  },
  async loadAlgoliaConfig({commit}) {
    const algoliaConfig = {
      appId: '1TIGF75UT0',
      indexName: process.env.VUE_APP_GCP_ENV === 'production' ? 'prod_shareableMaps' : 'dev_shareableMaps',
      apiKey: await TokenService.getToken('algoliaKey'),
    };
    commit('setAlgoliaConfig', algoliaConfig);
  },
};

const baseGetters = {
  isInternal: () => process.env.VUE_APP_FLAVOR === 'internal',
  isExternal: () => process.env.VUE_APP_FLAVOR === 'external',
};

export {
  // Store
  baseGetters,
  baseState,
  baseMutations,
  baseActions,
  // Types
  type AppState,
  type BaseMutations,
  type BaseActions,
  type BaseActionContext,
  type AddableFeature,
  type MenuItem,
};
